import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCurrentUser, RBACService, INVENTORY_ACCESS_PERMISSIONS } from '../utils/rbac';
import { Image, Tooltip } from 'antd';
import {
  DashboardOutlined,
  TeamOutlined,
  UserAddOutlined,
  EnvironmentOutlined,
  ShoppingCartOutlined,
  InboxOutlined,
  BarChartOutlined,
  MenuOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useCompanyLogo } from '../hooks/useCompanyLogo';
import './AdminSidebar.css';

interface AdminSidebarProps {
  collapsed?: boolean;
  onCollapse?: (collapsed: boolean) => void;
  isMobile?: boolean;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ collapsed: controlledCollapsed, onCollapse, isMobile: isMobileProp }) => {
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const isControlled = controlledCollapsed !== undefined;
  const collapsed = isControlled ? controlledCollapsed : internalCollapsed;
  const isMobile = isMobileProp ?? (typeof window !== 'undefined' && window.innerWidth <= 768);

  const setCollapsed = (value: boolean) => {
    if (!isControlled) setInternalCollapsed(value);
    onCollapse?.(value);
  };
  const navigate = useNavigate();
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState(getCurrentUser());
  const companyLogo = useCompanyLogo(currentUser?.id ?? null);

  // Keep user in sync (e.g. after login)
  useEffect(() => {
    const sync = () => setCurrentUser(getCurrentUser());
    window.addEventListener('storage', sync);
    window.addEventListener('sessionRestored', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('sessionRestored', sync);
    };
  }, []);

  const roleLabel = (currentUser?.roleName || 'User').trim();
  const panelTitle = 'Admin Portal';
  const getRoleInitials = () => {
    const parts = roleLabel.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return roleLabel.substring(0, 2).toUpperCase();
  };

  const sectionSubtitle = roleLabel;

  const isCompanyAdmin = RBACService.isCompanyAdmin();
  const menuItems = [
    { key: '/admin-portal/dashboard', icon: <DashboardOutlined />, label: 'Dashboard', permission: 'dashboard_view' },
    { key: '/admin-portal/users', icon: <TeamOutlined />, label: 'User Management', permission: 'user_read' },
    { key: '/admin-portal/users/add', icon: <UserAddOutlined />, label: 'Add User', permission: 'user_create' },
    { key: '/admin-portal/locations', icon: <EnvironmentOutlined />, label: 'Location Management', permission: 'location_read' },
    { key: '/admin-portal/purchase-orders', icon: <ShoppingCartOutlined />, label: 'Purchase Orders', permission: 'purchase_read' },
    { key: '/admin-portal/inventory', icon: <InboxOutlined />, label: 'Inventory', permissions: INVENTORY_ACCESS_PERMISSIONS },
    { key: '/admin-portal/reports/sales', icon: <BarChartOutlined />, label: 'Sales Report', permission: 'report_view' },
    { key: '/admin-portal/reports/gst', icon: <BarChartOutlined />, label: 'GST Filing', permission: 'report_view' },
    { key: '/admin-portal/reports/tds', icon: <BarChartOutlined />, label: 'TDS Withheld', permission: 'report_view' },
    { key: '/admin-portal/reports/gl', icon: <BarChartOutlined />, label: 'General Ledger', permission: 'report_view' },
  ].filter((item) => {
    if (isCompanyAdmin) return true;
    if ('permissions' in item && item.permissions) {
      return RBACService.hasAnyPermission(item.permissions);
    }
    return RBACService.hasPermission(item.permission);
  });

  const handleMenuClick = (key: string) => {
    // When already on User Management and user clicks "User Management", close Add User modal
    if (key === '/admin-portal/users' && location.pathname.startsWith('/admin-portal/users')) {
      window.dispatchEvent(new CustomEvent('admin-close-add-user-modal'));
    }
    navigate(key);
    if (isMobile) setCollapsed(true);
  };

  const isActive = (key: string) => {
    const path = location.pathname || '';
    if (key === '/admin-portal/dashboard' || key === '/admin-portal') {
      return path === '/admin-portal/dashboard' || path === '/admin-portal';
    }
    if (key === '/admin-portal/users/add') {
      return path === '/admin-portal/users/add';
    }
    if (key === '/admin-portal/users') {
      return path === '/admin-portal/users' || path === '/admin-portal/users/';
    }
    return path === key || path.startsWith(key + '/');
  };

  return (
    <>
      {!collapsed && isMobile && (
        <div
          className="admin-sidebar-backdrop"
          onClick={() => setCollapsed(true)}
        />
      )}
      <div className={`admin-sidebar ${collapsed ? 'collapsed' : ''} ${isMobile ? 'admin-sidebar-mobile' : ''}`}>
        {/* Header Section */}
        <div className="admin-sidebar-header">
          <div className="admin-sidebar-logo">
            {companyLogo ? (
              <div className="admin-logo-imageWrap">
                <Image
                  src={companyLogo}
                  alt="Company Logo"
                  preview={false}
                  className="admin-logo-image"
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                />
              </div>
            ) : (
              <div className="admin-logo-icon">{getRoleInitials()}</div>
            )}
          </div>
          {!collapsed && (
            <div className="admin-sidebar-title-section">
              <div className="admin-sidebar-title">{panelTitle}</div>
              <div className="admin-sidebar-subtitle">{sectionSubtitle}</div>
            </div>
          )}
          <button
            type="button"
            className="admin-sidebar-close"
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? 'Open menu' : 'Close sidebar'}
          >
            <span className="admin-sidebar-close-icon">
              {collapsed ? <MenuOutlined /> : <CloseOutlined />}
            </span>
          </button>
        </div>

      {/* Navigation Items */}
      <div className="admin-sidebar-menu">
        {menuItems.map((item) => (
          <Tooltip
            key={item.key}
            title={collapsed ? item.label : ''}
            placement="right"
            overlayInnerStyle={{ fontSize: 14 }}
          >
            <div
              className={`admin-menu-item ${isActive(item.key) ? 'active' : ''}`}
              onClick={() => handleMenuClick(item.key)}
            >
              <span className="admin-menu-icon">{item.icon}</span>
              {!collapsed && <span className="admin-menu-label">{item.label}</span>}
            </div>
          </Tooltip>
        ))}
      </div>

      {/* Footer Section - User Info */}
      {!collapsed && (
        <div className="admin-sidebar-footer">
          <div className="admin-user-icon">{getRoleInitials()}</div>
          <div className="admin-user-info">
            <div className="admin-user-name">{currentUser?.name || 'Admin User'}</div>
            <div className="admin-user-email">{(currentUser?.email || 'admin@company.com').replace(/^["']|["']$/g, '')}</div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default AdminSidebar;
